import { AxiosInstance, ResponseType } from "axios";
import { getApiInstance } from "./apiCore";

let sharedBaseApi: BaseApi | null = null;

/** Single shared BaseApi instance. Use in custom API modules instead of local getApiInstance(). */
export function getBaseApi(): BaseApi {
  if (!sharedBaseApi) {
    sharedBaseApi = new BaseApi();
  }
  return sharedBaseApi;
}

// In-memory TTL cache shared across all BaseApi instances. Keyed by
// `${url}::${JSON.stringify(params)}` so different query params never collide.
// Mutations (POST/PUT/DELETE) call BaseApi.invalidateCache(prefix) to drop
// stale entries and force the next list load to refetch.
type CachedEntry = { data: any; ts: number; ttl: number };
const responseCache = new Map<string, CachedEntry>();
const cacheKey = (url: string, params?: any) =>
  `${url}::${params ? JSON.stringify(params) : ""}`;

/** Uses shared axios instance from apiCore - single source of truth, token refresh, timeout */
export class BaseApi {
  protected responseType: ResponseType;
  protected headersAccept: string;
  public axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = getApiInstance();
    this.responseType = "json";
    this.headersAccept = "application/json";
  }

  public setResponseType(responseType: ResponseType) {
    this.responseType = responseType;
  }

  public setHeadersAccept(headersAccept: string) {
    this.headersAccept = headersAccept;
  }

  async get(url: string, params?: any, headers?: any): Promise<any> {
    return this.axiosInstance.request({
      method: "GET",
      url,
      params: params || undefined,
      responseType: this.responseType,
      headers: { Accept: this.headersAccept, ...(headers || {}) },
    });
  }

  /**
   * Cached GET — returns a stored response if fetched less than `ttlMs` ago
   * with the same URL+params, otherwise fires the request and caches it.
   * Use for idempotent list/summary endpoints. Skip for user-triggered
   * refreshes (call `.get()` or pass force=true).
   */
  async getCached(
    url: string,
    params?: any,
    ttlMs: number = 30_000,
    force: boolean = false,
    headers?: any
  ): Promise<any> {
    const key = cacheKey(url, params);
    const now = Date.now();
    if (!force) {
      const hit = responseCache.get(key);
      if (hit && now - hit.ts < hit.ttl) {
        // Preserve axios-style response shape so callers don't have to branch.
        return { data: hit.data, status: 200, cached: true, headers: {} };
      }
    }
    const res = await this.get(url, params, headers);
    if (res?.__handled403 || res?.status === 403 || res?.data == null) {
      return res;
    }
    responseCache.set(key, { data: res?.data, ts: now, ttl: ttlMs });
    return res;
  }

  /**
   * Drop any cached entries whose key starts with the given URL prefix. Call
   * from mutating helpers (create/update/delete) so the next list load
   * shows fresh data instead of the cached one.
   */
  static invalidateCache(urlPrefix: string): void {
    for (const k of Array.from(responseCache.keys())) {
      if (k.startsWith(urlPrefix)) {
        responseCache.delete(k);
      }
    }
  }

  async post(
    url: string,
    data?: any,
    params?: any,
    headers?: any
  ): Promise<any> {
    return this.axiosInstance.request({
      method: "POST",
      url,
      data: data || undefined,
      params: params || undefined,
      headers: { Accept: this.headersAccept, ...(headers || {}) },
    });
  }

  async put(
    url: string,
    data?: any,
    params?: any,
    headers?: any
  ): Promise<any> {
    return this.axiosInstance.request({
      method: "PUT",
      url,
      data: data || undefined,
      params: params || undefined,
      headers: { Accept: this.headersAccept, ...(headers || {}) },
    });
  }

  async delete(url: string, params?: any, headers?: any): Promise<any> {
    return this.axiosInstance.request({
      method: "DELETE",
      url,
      params: params || undefined,
      headers: { Accept: this.headersAccept, ...(headers || {}) },
    });
  }

  async downloadFile(url: string, fileName: string): Promise<void> {
    try {
      const response = await this.axiosInstance.get(url, {
        responseType: "arraybuffer",
        headers: {},
      });

      const contentType =
        String(response.headers["content-type"] ?? "application/octet-stream");
      const blob = new Blob([response.data], { type: contentType });
      this.saveFile(blob, fileName);
    } catch (error) {
      console.error("Error downloading file:", error);
      throw new Error("File download failed.");
    }
  }

  async downloadFileForm(
    url: string,
    body: any,
    fileName: string,
    responseType: any = "arraybuffer"
  ): Promise<void> {
    try {
      const response = await this.axiosInstance.post(url, body, {
        responseType,
        headers: {},
      });

      if (responseType === "arraybuffer") {
        const contentType =
          String(response.headers["content-type"] ?? "application/octet-stream");
        const blob = new Blob([response.data], { type: contentType });
        this.saveFile(blob, fileName);
      } else if (responseType === "text") {
        // Text response - caller handles display
        const blob = new Blob([response.data], { type: "text/plain" });
        this.saveFile(blob, fileName);
      } else {
        console.warn("Unhandled response type:", responseType);
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      throw new Error("File download failed.");
    }
  }

  private saveFile(blob: Blob, fileName: string): void {
    try {
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error saving file:", error);
      throw new Error("File save failed.");
    }
  }
}